import { Grid, Stack, Typography } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useCurrency } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { BaseDetailDomainType, ContractDetailsData, I18nNamespace } from '~/common/types';
import {
  ColumnSelector,
  DataGridProStyled,
  DataGridWidgetHeader,
  DensityButton,
  ResetRemoteStateButton,
  useDataGridRemoteState,
} from '~/components/DataGridCustom';

import { useContractDetailsDataGridConfig } from './ContractDetails.dataGrid';

const domain = BaseDetailDomainType.CONTRACT_DETAILS;

export interface ContractDetailsProps {
  data?: Partial<ContractDetailsData> | null;
  loading?: boolean;
}

export const ContractDetails = ({ data, loading }: ContractDetailsProps) => {
  const { format: formatCurrency } = useCurrency();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ContractDetailsWidget' });

  const apiRef = useGridApiRef();
  const config = useContractDetailsDataGridConfig();

  const {
    loading: loadingRemoteState,
    gridProps,
    onDensityChange,
    onRemoteStateReset,
  } = useDataGridRemoteState({
    apiRef,
    config,
    domain,
  });

  const items = data?.items;
  const plannedContractValue = data?.plannedContractValue;
  const totalPlannedValue = formatCurrency(plannedContractValue ?? 0);

  const skip = loadingRemoteState || !items;

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={4}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          alignContent: 'flex-start',
        }}
      >
        {(items && items.length) || loading ? (
          <Grid size={12}>
            {plannedContractValue !== undefined && (
              <Typography
                variant="body2"
                color="textSecondary"
                gutterBottom
                sx={{
                  display: 'block',
                }}
              >
                {t('totalVolume', { totalPlannedValue })}
              </Typography>
            )}
            <DataGridWidgetHeader
              disablePadding
              left={t('positions')}
              right={
                <Stack direction="row">
                  <DensityButton density={gridProps.density} onDensityChange={onDensityChange} />
                  <ColumnSelector
                    columns={gridProps.columns}
                    columnVisibilityModel={gridProps.columnVisibilityModel}
                    onColumnVisibilityModelChange={gridProps.onColumnVisibilityModelChange}
                  />
                  <ResetRemoteStateButton onRemoteStateReset={onRemoteStateReset} />
                </Stack>
              }
            />
            {skip ? null : (
              <DataGridProStyled
                {...config}
                apiRef={apiRef}
                autoHeight
                hideFooter
                disableColumnMenu
                loading={loading}
                rows={items}
                rowSelection={false}
                {...gridProps}
              />
            )}
          </Grid>
        ) : null}
      </Grid>
    </>
  );
};
